import { Suspense } from "react";
import { lazyRetry } from "@/lib/lazy-retry";
import { Loader } from "@/components/shared/Loader";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { MotionConfig } from "motion/react";
import { CountryProvider } from "@/contexts/CountryContext";
import { AuthModalProvider, useAuthModal } from "@/contexts/AuthModalContext";
// AuthProvider + ApplicationProvider are lazy-loaded (not needed on initial public page render)
import { Navbar } from "@/components/sections/Navbar";
import { FooterRedesigned } from "@/components/landing/FooterRedesigned";
import { StickyMobileCTA } from "@/components/landing/StickyMobileCTA";
// AuthModal is lazy-loaded with its own AuthProvider (keeps Supabase out of main bundle)
const LazyAuthModal = lazyRetry(() => import("@/components/AuthModalWithAuth"));
import { ScrollToTop } from "@/components/ScrollToTop";
import { AnalyticsTracker } from "@/components/AnalyticsTracker";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

// Only the homepage is eagerly loaded
import HomePage from "@/pages/HomePage";

// Legal pages
const Terms = lazyRetry(() =>
  import("@/pages/Terms").then((m) => ({ default: m.Terms }))
);
const Privacy = lazyRetry(() =>
  import("@/pages/Privacy").then((m) => ({ default: m.Privacy }))
);
const CookiePolicyPage = lazyRetry(() =>
  import("@/pages/CookiePolicy").then((m) => ({ default: m.CookiePolicy }))
);

// Public pages
const BlogIndex = lazyRetry(() =>
  import("@/pages/BlogIndex").then((m) => ({ default: m.BlogIndex }))
);
const BlogArticle = lazyRetry(() =>
  import("@/pages/BlogArticle").then((m) => ({ default: m.BlogArticle }))
);
const FAQ = lazyRetry(() =>
  import("@/pages/FAQ").then((m) => ({ default: m.FAQ }))
);
const GuidesPage = lazyRetry(() =>
  import("@/pages/Guides").then((m) => ({ default: m.GuidesPage }))
);
const Contact = lazyRetry(() =>
  import("@/pages/Contact").then((m) => ({ default: m.Contact }))
);
const Universities = lazyRetry(() =>
  import("@/pages/Universities").then((m) => ({ default: m.Universities }))
);
const Impressum = lazyRetry(() =>
  import("@/pages/Impressum").then((m) => ({ default: m.Impressum }))
);
const BuddyAgreement = lazyRetry(() =>
  import("@/pages/BuddyAgreement").then((m) => ({ default: m.BuddyAgreement }))
);
const CommunityPage = lazyRetry(() =>
  import("@/pages/Community").then((m) => ({ default: m.CommunityPage }))
);
const VisionTerms = lazyRetry(() =>
  import("@/pages/VisionTerms").then((m) => ({ default: m.VisionTerms }))
);

// Auth callback (OAuth redirect landing)
const AuthCallback = lazyRetry(() =>
  import("@/pages/AuthCallback").then((m) => ({ default: m.AuthCallback }))
);

// Private shell: auth guard for /app (lazy-loaded)
const PrivateShell = lazyRetry(() => import("@/layouts/PrivateShell"));

// The app: the student's case (lazy-loaded)
const AppShell = lazyRetry(() => import("@/case/AppShell").then((m) => ({ default: m.AppShell })));
const Claim = lazyRetry(() => import("@/case/Claim").then((m) => ({ default: m.Claim })));
const Start = lazyRetry(() => import("@/pages/Start"));
const Ru = lazyRetry(() => import("@/pages/Ru"));
const EmailPreferences = lazyRetry(() => import("@/pages/EmailPreferences"));

// Country/city landing pages (e.g., /india, /india/vienna)
const CountryLandingPage = lazyRetry(() => import("@/pages/CountryLandingPage"));

// 404
const NotFound = lazyRetry(() =>
  import("@/pages/NotFound").then((m) => ({ default: m.NotFound }))
);

/** Lazy-loads AuthModal + AuthProvider only when the modal is opened */
function ConditionalAuthModal() {
  const { isOpen } = useAuthModal();
  if (!isOpen) return null;
  return (
    <Suspense fallback={null}>
      <LazyAuthModal />
    </Suspense>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
    {/* reducedMotion="user" makes every Framer Motion animation in the app
        honour the OS "reduce motion" setting. Pairs with the CSS block in
        index.css, which covers the non-Framer animations. */}
    <MotionConfig reducedMotion="user">
    <HelmetProvider>
    <BrowserRouter>
      <CountryProvider>
          <AuthModalProvider>
              <ScrollToTop />
              <AnalyticsTracker />
              <div
                className="min-h-screen bg-[var(--color-bg-primary)] antialiased"
                lang="en"
              >
                <Suspense fallback={<Loader />}>
                  <Routes>
                    {/* Auth callback — spinner only, no layout */}
                    <Route path="/auth/callback" element={<AuthCallback />} />

                    {/* The app, behind the auth guard */}
                    <Route path="/onboarding" element={<Navigate to="/app" replace />} />
                    <Route element={<PrivateShell />}>
                      <Route path="/app/*" element={<AppShell />} />
                    </Route>
                    {/* "Get visa help": the plan request on its own page, no site chrome */}
                    <Route path="/start" element={<Start />} />
                    {/* The Austria check in Russian, its own calm page like /start */}
                    <Route path="/ru" element={<Ru />} />
                    {/* A claim link from the "your case is open" email */}
                    <Route path="/claim" element={<Claim />} />
                    {/* "Email settings" from the bottom of every email: no login, the link is the key */}
                    <Route path="/email" element={<EmailPreferences />} />

                    {/* Public pages — lightweight, no ApplicationProvider */}
                    <Route
                      path="*"
                      element={
                        <>
                          <Navbar />
                          <main>
                            <Routes>
                              <Route path="/" element={<HomePage />} />
                              <Route path="/blog" element={<BlogIndex />} />
                              <Route path="/blog/:slug" element={<BlogArticle />} />
                              <Route path="/faq" element={<FAQ />} />
                              <Route path="/guides" element={<GuidesPage />} />
                              <Route path="/visa-check" element={<Navigate to="/start" replace />} />
                              <Route path="/community" element={<CommunityPage />} />
                              <Route path="/community/:slug" element={<CommunityPage />} />
                              <Route path="/contact" element={<Contact />} />
                              <Route path="/terms" element={<Terms />} />
                              <Route path="/privacy" element={<Privacy />} />
                              <Route path="/universities" element={<Universities />} />
                              <Route path="/impressum" element={<Impressum />} />
                              <Route path="/buddy-agreement" element={<BuddyAgreement />} />
                              <Route path="/cookies" element={<CookiePolicyPage />} />
                              <Route path="/visa-terms" element={<VisionTerms />} />
                              <Route path="/:country" element={<CountryLandingPage />} />
                              <Route path="/:country/:city" element={<CountryLandingPage />} />
                              <Route path="*" element={<NotFound />} />
                            </Routes>
                          </main>
                          <FooterRedesigned />
                          <StickyMobileCTA />
                        </>
                      }
                    />
                  </Routes>
                </Suspense>
                <ConditionalAuthModal />
                <Toaster />
              </div>
          </AuthModalProvider>
      </CountryProvider>
    </BrowserRouter>
    </HelmetProvider>
    </MotionConfig>
    </ErrorBoundary>
  );
}
